// PR-32: build the wire payload sent to the agent when the user is
// "discussing" a pinned gap. Mirror of `buildDiscussingGapWire` in
// web/src/App.tsx (line 75) — kept duplicated rather than imported so
// PR-32 doesn't refactor the desktop component. If the duplication
// becomes a maintenance hotspot, lift to a shared util.
//
// Mobile only emits the regular (non-opener) flavor in PR-32; the opener
// path is gap-discuss-thread creation, which lands in PR-34.
//
// `stripDiscussingGapWire` and `isGapOpenerWire` are imported by both
// MobileShell and App.tsx on reload — the persisted user message is the
// raw wireText, but the bubble must show the original user input. Lifted
// here (instead of duplicated again) because reload paths on both shells
// hit this exact code at the same time.

import type { Card } from '../../api'

/** Marker suffix on the synthetic opener turn fired automatically when
 *  the user clicks Discuss on a gap. The opener turn lives as a user-role
 *  message on the server (the LLM transcript needs SOMETHING to drive a
 *  turn), but the FE must skip rendering it as a bubble on reload. */
const GAP_OPENER_PREFIX = /^\[discussing-gap c-\w+ — opener\]/
const DISCUSSING_GAP_PREFIX = /^\[discussing-gap c-\w+\]/
const USER_SEPARATOR = '\n\n---\n\nUSER: '

/** True when this user message is a synthetic opener (auto-fired on
 *  Discuss click). Reload paths skip these so they don't render as a
 *  user bubble. */
export function isGapOpenerWire(text: string): boolean {
  return GAP_OPENER_PREFIX.test(text)
}

/** Recover the user-typed text from a persisted user message that was
 *  sent through `buildDiscussingGapWire`. The wire format wraps the
 *  user input in a `[discussing-gap c-X] ... USER: <text>` envelope so
 *  the agent sees full gap context; the bubble must show only `<text>`.
 *
 *  Returns:
 *   - `null` for synthetic opener turns (caller should skip the bubble)
 *   - the unwrapped user text for the regular `[discussing-gap c-X]` envelope
 *   - the original input for messages not wrapped at all
 *
 *  Falls back to the original text if the envelope is malformed (no
 *  separator) — better to show too much than to drop the bubble entirely. */
export function stripDiscussingGapWire(text: string): string | null {
  if (isGapOpenerWire(text)) return null
  if (!DISCUSSING_GAP_PREFIX.test(text)) return text
  const idx = text.lastIndexOf(USER_SEPARATOR)
  if (idx < 0) return text
  return text.slice(idx + USER_SEPARATOR.length)
}

/**
 * See App.tsx:buildDiscussingGapWire for the full rationale. Summary:
 * the opener turn (one per discussing-gap thread) sends full gap context;
 * subsequent turns send a slim `[discussing-gap c-X] ... USER: <text>`
 * routing envelope. The cards-snapshot volatile reminder carries gap
 * title + body summary every turn, so repeating the full body per turn
 * is duplication that prefix caching can't recover.
 */
export function buildDiscussingGapWire(
  gap: Card,
  allCards: Card[],
  userText: string,
  options?: { opener?: boolean },
): string {
  const opener = options?.opener === true

  if (!opener) {
    return `[discussing-gap ${gap.card_id}]\n\n---\n\nUSER: ${userText}`
  }

  const proposedKind =
    typeof gap.extra?.['proposed_kind'] === 'string'
      ? (gap.extra['proposed_kind'] as string)
      : 'card'
  const ambiguity =
    typeof gap.extra?.['ambiguity_reason'] === 'string'
      ? (gap.extra['ambiguity_reason'] as string)
      : null
  const blockIds = Array.isArray(gap.extra?.['blocks'])
    ? (gap.extra['blocks'] as unknown[]).filter(
        (x): x is string => typeof x === 'string',
      )
    : []

  const blockSummaries = blockIds.map((id) => {
    const c = allCards.find((c) => c.card_id === id)
    if (!c) return `${id}: (not in cards)`
    const title =
      c.title?.trim() ||
      c.body
        .split('\n')
        .find((l) => l.trim().length > 0)
        ?.trim()
        .slice(0, 60) ||
      '(untitled)'
    return `${id} (${c.kind}): ${title}`
  })

  const lines: string[] = [
    `[discussing-gap ${gap.card_id} — opener] — full gap context follows; produce the opener described at the end. The user has NOT typed anything yet.`,
    '',
    `GAP TITLE: ${gap.title?.trim() || '(untitled)'}`,
    `PROPOSED KIND ON APPROVE: ${proposedKind}`,
  ]
  if (ambiguity) lines.push(`AMBIGUITY REASON: ${ambiguity}`)
  if (blockSummaries.length > 0) {
    lines.push('BLOCKS:')
    for (const s of blockSummaries) lines.push(`  - ${s}`)
  }
  if (gap.body.trim()) {
    lines.push('', 'GAP BODY:', gap.body.trimEnd())
  }
  lines.push(
    '',
    '---',
    '',
    'OPENER INSTRUCTION:',
    'The user just clicked Discuss on this gap. Open the conversation in one short message:',
    '  1. Restate what the gap is asking in one sentence.',
    '  2. Survey related cards already in the project — what is defined, what is blank. Cite by [[c-X]].',
    '  3. List 3–5 concrete sub-questions to decide.',
    '  4. End with one open question: where does the user want to start?',
    'Keep it terse (under ~200 words). Do NOT write any cards yet — frame only.',
  )
  return lines.join('\n')
}
